import { useEffect, useRef } from 'react'

import Pusher from 'pusher-js'

import { PUSHER_CLUSTERS, PUSHER_KEYS } from 'common/constants/siteSelectorConstants'

import {
  parseEncounterCheckoutPayload,
  type EncounterCheckoutPusherPayload,
} from './encounter-checkout-pusher.utils'

const CHECKOUT_EVENT = 'chart_checkout_update'

type Options = {
  siteId: string
  encounterUid: string
  enabled: boolean
  generation?: number
  onUpdate: (payload: EncounterCheckoutPusherPayload) => void
}

export const useEncounterCheckoutPusher = ({
  siteId,
  encounterUid,
  enabled,
  generation = 0,
  onUpdate,
}: Options): void => {
  const onUpdateRef = useRef(onUpdate)
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  useEffect(() => {
    if (!enabled || !siteId || !encounterUid) {
      return
    }

    const pusherKey = PUSHER_KEYS[siteId]
    const pusherCluster = PUSHER_CLUSTERS[siteId]
    if (!pusherKey || !pusherCluster) {
      return
    }

    const pusher = new Pusher(pusherKey, { cluster: pusherCluster })
    const channel = pusher.subscribe(encounterUid)

    const handler = (rawEvent: unknown) => {
      const parsed = parseEncounterCheckoutPayload(rawEvent)
      if (!parsed) return
      onUpdateRef.current(parsed)
    }

    channel.bind(CHECKOUT_EVENT, handler)

    return () => {
      channel.unbind(CHECKOUT_EVENT, handler)
      pusher.unsubscribe(encounterUid)
      pusher.disconnect()
    }
  }, [enabled, siteId, encounterUid, generation])
}
